/**
 * MontaTech v2 - Main Component
 *
 * Ferramenta de nesting/encaixe de objetos
 * Fluxo unico C# nativo
 */

import { useMontaTechState } from './hooks';
import { StatusBar, ActionBar, PreviewCanvas, SettingsModal } from './components';

interface MontaTechPageProps {
  onPopout?: () => void;
  onBack?: () => void;
}

export function MontaTechPage({ onPopout, onBack }: MontaTechPageProps = {}) {
  const state = useMontaTechState({ onApplySuccess: onBack });

  return (
    <div className="h-full flex flex-col bg-navy-900">
      {/* Canvas area principal */}
      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        {/* Barra de status com seletor de perfil direto */}
        <StatusBar
          selectedProfileName={state.profiles.selectedProfileName}
          isLocalProfile={state.profiles.isLocalProfile}
          profiles={state.profiles.profiles}
          activeProfiles={state.profiles.activeProfiles}
          apiProfiles={state.profiles.apiProfiles}
          localProfiles={state.profiles.localProfiles}
          transform={state.canvas.transform}
          toolVersion={state.toolVersion}
          onSelectProfile={state.profiles.selectProfile}
          onOpenSettings={() => state.setShowSettings(true)}
          onZoomReset={state.canvas.resetTransform}
          onPopout={onPopout}
        />

        {/* Canvas de preview */}
        <PreviewCanvas
          containerRef={state.containerRef}
          canvas={state.canvas}
          status={state.status}
          error={state.error}
          curveData={state.curveData}
          progress={state.progress}
          progressLabel={state.progressLabel}
          isRecovering={state.isRecovering}
          emptyState={state.emptyState}
          nestingResult={state.nestingResult}
          maximumY={state.maximumY}
          boundsWarning={state.boundsWarning}
          onRetryCollect={state.collectShapes}
        />
      </div>

      {/* Barra de acoes */}
      <ActionBar
        status={state.status}
        canCollect={state.canCollect}
        canCalculate={state.canCalculate}
        canOptimize={state.canOptimize}
        canApply={state.canApply}
        canCancel={state.canCancel}
        hasResult={!!state.nestingResult && state.nestingResult.length > 0}
        isProcessing={state.isProcessing}
        onCollect={state.collectShapes}
        onCalculate={state.calculateNesting}
        onOptimize={state.optimizeResult}
        onApply={state.applyToCorelDraw}
        onCancel={state.cancelCalculation}
        onReset={state.reset}
      />

      {/* Modal de Configuracoes */}
      <SettingsModal
        isOpen={state.showSettings}
        activeTab={state.settingsTab}
        profiles={state.profiles.profiles}
        activeProfiles={state.profiles.activeProfiles}
        apiProfiles={state.profiles.apiProfiles}
        localProfiles={state.profiles.localProfiles}
        selectedProfileName={state.profiles.selectedProfileName}
        editingProfile={state.profiles.editingProfile}
        isLocalProfile={state.profiles.isLocalProfile}
        isLoading={state.profiles.isLoading}
        onClose={() => state.setShowSettings(false)}
        onTabChange={state.setSettingsTab}
        onSelectProfile={state.profiles.selectProfile}
        onUpdateProfile={state.profiles.updateEditingProfile}
        onSaveProfile={state.profiles.saveAsLocalProfile}
        onDeleteProfile={state.profiles.deleteProfile}
      />
    </div>
  );
}

// Re-export types for external use
export * from './types';
export * from './constants';
